import { cn } from "@loot-os/ui/lib/utils";
import type { ReactNode } from "react";

/** One set of box art: the image, what sits behind it and what sits over it. "" = none. */
export interface BoxArtLayers {
  image: string;
  background: string;
  overlay: string;
}

/**
 * Box art as layers: the background fills the frame, the box image sits centered on it, and the
 * overlay covers both. Each layer is optional.
 *
 * `mobile` is the phone set. Rendered responsively, each mobile layer replaces its desktop one
 * below the `sm` breakpoint through `<picture>`, and an empty mobile layer keeps the desktop one.
 * `show` pins one set, for an editor previewing both side by side.
 */
export function BoxArt({
  desktop,
  mobile,
  show = "responsive",
  alt = "",
  className,
  fallback,
}: {
  desktop: BoxArtLayers;
  mobile?: Partial<BoxArtLayers>;
  show?: "responsive" | "desktop" | "mobile";
  alt?: string;
  className?: string;
  /** Centered on the muted frame when there is no image at all. */
  fallback?: ReactNode;
}) {
  const layer = (key: keyof BoxArtLayers): { desktop: string; mobile: string } => {
    const phone = mobile?.[key] || desktop[key];
    if (show === "desktop") return { desktop: desktop[key], mobile: desktop[key] };
    if (show === "mobile") return { desktop: phone, mobile: phone };
    return { desktop: desktop[key], mobile: phone };
  };
  const background = layer("background");
  const image = layer("image");
  const overlay = layer("overlay");
  const empty = !background.desktop && !image.desktop && !overlay.desktop && !image.mobile;

  return (
    <div className={cn("relative overflow-hidden bg-muted", className)}>
      <Layer src={background} className="absolute inset-0 size-full object-cover" />
      <Layer src={image} alt={alt} className="absolute inset-0 m-auto size-full object-contain" />
      <Layer src={overlay} className="absolute inset-0 size-full object-contain" />
      {empty && fallback && (
        <span aria-hidden className="absolute inset-0 flex items-center justify-center">
          {fallback}
        </span>
      )}
    </div>
  );
}

function Layer({
  src,
  alt = "",
  className,
}: {
  src: { desktop: string; mobile: string };
  alt?: string;
  className: string;
}) {
  if (!src.desktop && !src.mobile) return null;
  if (!src.desktop || src.desktop === src.mobile) {
    return <img src={src.mobile || src.desktop} alt={alt} className={className} loading="lazy" />;
  }
  return (
    <picture>
      {src.mobile && <source media="(max-width: 639px)" srcSet={src.mobile} />}
      <img src={src.desktop} alt={alt} className={className} loading="lazy" />
    </picture>
  );
}
